import { useEffect, useState } from "react";
import { FaPlus, FaTrash } from "react-icons/fa";
import { VAULT_CONSTANTS } from "../_MODULES/_API";
import { OPTIONS_VAULT_ENTRY_TYPE, OPTIONS_VAULT_ENVIRONMENT, OPTIONS_CODE_LANGUAGE } from "../../../_Catalogs.Views";
import { AppModal } from "../../../../../_CORE/APP/Templates/_UI/Modal/_index";
import { AppButton } from "../../../../../_CORE/APP/Templates/_UI/Button/_index";
import { FormGrid, FormSection, FieldInput, FieldSelect, FieldTextarea, FieldCode, FieldRichText, FieldSwitch, useForm } from "../../../../../_CORE/APP/Templates/_UI/Form/_index";
import { useApiActionMutation } from "../../../../../_CORE/APP/Hooks/Hooks/_Actions/_useApiActionMutation";
import {
    CredentialSpecializedSections, onEmptyCredentialTechnical, onSplitCredentialTechnicalExtra,
    onMergeCredentialTechnicalExtra,
} from "../../../../_UI/CredentialSpecializedSections";

const EMPTY_EXTRA = { label: "", value: "", isSecret: false };

const EMPTY_VALUES = {
    parentId: "", title: "", type: "SERVER", environment: "", host: "", port: "", url: "",
    email: "", username: "", secret: "", passwordHint: "", token: "", connectionString: "", sshKey: "", databaseName: "",
    notes: "", command: "", commandLanguage: "bash", isFavorite: false,
};

const onEntryDescendantIds = (entry) => {
    const ids = new Set();
    const visit = (items = []) => items.forEach((item) => {
        ids.add(String(item.id));
        visit(item.children ?? []);
    });
    visit(entry?.children ?? []);
    return ids;
};

export const VaultEntryFormModal = ({
    isOpen, groupId, entry = null, revealed = null, accessPin = null,
    parentOptions = [], defaultParentId = null, isStacked = false, onClose, onSaved,
}) => {

    const [useExtra, setExtra] = useState([]);
    const [useTechnical, setTechnical] = useState(onEmptyCredentialTechnical);

    const IS_EDIT = !!entry?.id;

    const { actionMutate, useActionApiIsLoading } = useApiActionMutation({
        isToastError: true, isToastSuccess: true, minTimeMutate: 400,
    });

    const FORM = useForm({
        initialValues: EMPTY_VALUES,

        schema: { title: { required: true, minLength: 2 } },

        onSubmit: async (values) => {

            const PAYLOAD = {
                parentId: values.parentId || null,
                title: values.title.trim(),
                extra: onMergeCredentialTechnicalExtra(
                    useExtra.filter((field) => !!field.label.trim()),
                    useTechnical,
                ),
                type: values.type,
                environment: values.environment || "",
                host: values.host || "",
                port: values.port || "",
                url: values.url || "",
                email: values.email || "",
                username: values.username || "",
                databaseName: values.databaseName || "",
                notes: values.notes || "",
                command: values.command || "",
                commandLanguage: values.commandLanguage,
                isFavorite: !!values.isFavorite,
                accessPin: accessPin ?? undefined,
            };

            if (!IS_EDIT) PAYLOAD.groupId = groupId;

            PAYLOAD.passwordHint = values.passwordHint ?? "";

            ["secret", "token", "connectionString", "sshKey"].forEach((field) => {

                if (values[field]) {
                    PAYLOAD[field] = values[field];
                    return;
                }

                if (IS_EDIT && revealed) PAYLOAD[field] = "";
            });

            const RESPONSE = IS_EDIT
                ? await actionMutate({ method: "PATCH", endpoint: VAULT_CONSTANTS.ENTRY_UPDATE, successMessage: "Acceso actualizado", id: entry.id, data: PAYLOAD })
                : await actionMutate({ method: "POST", endpoint: VAULT_CONSTANTS.ENTRY_CREATE, successMessage: "Acceso guardado", data: PAYLOAD });

            if (!RESPONSE.success) { FORM.applyApiError(RESPONSE); return; }

            onSaved?.(RESPONSE.data, IS_EDIT);
            onClose?.();
        },
    });

    useEffect(() => {

        if (!isOpen) return;

        if (IS_EDIT) {
            const SPLIT_EXTRA = onSplitCredentialTechnicalExtra(revealed?.extra ?? entry.extra ?? []);
            setExtra(SPLIT_EXTRA.custom);
            setTechnical(SPLIT_EXTRA.technical);
        } else {
            setExtra([]);
            setTechnical(onEmptyCredentialTechnical());
        }

        FORM.setValues(IS_EDIT ? {
            parentId: entry.parentId ?? "",
            title: entry.title ?? "",
            type: entry.type ?? "SERVER",
            environment: entry.environment ?? "",
            host: entry.host ?? "",
            port: entry.port ?? "",
            url: entry.url ?? "",
            email: entry.email ?? "",
            username: entry.username ?? "",
            databaseName: entry.databaseName ?? "",
            notes: entry.notes ?? "",
            command: entry.command ?? "",
            commandLanguage: entry.commandLanguage ?? "bash",
            isFavorite: !!entry.isFavorite,
            secret: revealed?.secret ?? "",
            passwordHint: revealed?.passwordHint ?? entry.passwordHint ?? "",
            token: revealed?.token ?? "",
            connectionString: revealed?.connectionString ?? "",
            sshKey: revealed?.sshKey ?? "",
        } : { ...EMPTY_VALUES, parentId: defaultParentId ?? "" });

    }, [isOpen, entry, revealed]); // eslint-disable-line react-hooks/exhaustive-deps

    const onChangeExtra = (index, field, value) => {
        setExtra((prev) => prev.map((row, position) => (
            position === index ? { ...row, [field]: value } : row
        )));
    };

    const onChangeTechnical = (field, value) => {
        setTechnical((prev) => ({ ...prev, [field]: value }));
    };

    const onAddExtra = () => setExtra((prev) => [...prev, { ...EMPTY_EXTRA }]);

    const onRemoveExtra = (index) => setExtra((prev) => prev.filter((row, position) => position !== index));

    const BLOCKED_PARENT_IDS = onEntryDescendantIds(entry);
    if (entry?.id) BLOCKED_PARENT_IDS.add(String(entry.id));

    const HAS_CHILDREN = (entry?.children ?? []).length > 0;
    const PARENT_OPTIONS = [
        { value: "", label: "No depende de nada, va al nivel principal" },
        ...(HAS_CHILDREN ? [] : parentOptions
            .filter((option) => !BLOCKED_PARENT_IDS.has(String(option.value)))
            .map((option) => ({ ...option }))),
    ];

    const SECRET_PLACEHOLDER = IS_EDIT && !revealed ? "Sin cambios" : "";

    const ENVIRONMENT_OPTIONS = (() => {
        if (!FORM.values.environment || OPTIONS_VAULT_ENVIRONMENT.some((option) => option.value === FORM.values.environment)) {
            return OPTIONS_VAULT_ENVIRONMENT;
        }
        const LAST_OTHER_INDEX = OPTIONS_VAULT_ENVIRONMENT.findIndex((option) => option.value === "OTHER");
        if (LAST_OTHER_INDEX < 0) return [...OPTIONS_VAULT_ENVIRONMENT, { value: FORM.values.environment, label: FORM.values.environment }];
        return [
            ...OPTIONS_VAULT_ENVIRONMENT.slice(0, LAST_OTHER_INDEX),
            { value: FORM.values.environment, label: FORM.values.environment },
            ...OPTIONS_VAULT_ENVIRONMENT.slice(LAST_OTHER_INDEX),
        ];
    })();

    return (
        <AppModal
            isOpen={isOpen}
            onClose={onClose}
            className={isStacked ? "LStudio__app_nested_vault_child_form" : ""}
            isDirty={FORM.isDirty}
            size="xl"
            title={IS_EDIT ? "Editar acceso" : "Nuevo acceso"}
            description={
                IS_EDIT && !revealed
                    ? "Los secretos reales permanecen ocultos. La pista / palabra secreta sí se muestra completa para que puedas verificarla."
                    : "Contraseña, pista, token y cadena de conexión se guardan cifrados. La pista se muestra completa dentro de NOTEBOOK."
            }
            footer={
                <>
                    <AppButton variant="ghost" tone="neutral" onClick={onClose}>Cancelar</AppButton>
                    <AppButton onClick={FORM.onSubmit} isLoading={useActionApiIsLoading}>
                        {IS_EDIT ? "Guardar cambios" : "Guardar acceso"}
                    </AppButton>
                </>
            } >

            <div className="LStudio__app_priority_toggle">
                <FieldSwitch id="vault_entry_isFavorite" label="Destacado" text="Mostrar este acceso primero"
                    isChecked={!!FORM.values.isFavorite}
                    onChange={(event) => FORM.setValue("isFavorite", event.target.checked)} />
            </div>

            <FormSection title="Identificación">

                <FormGrid columns={3}>

                    <FieldInput {...FORM.field("title")} label="Título"
                        placeholder="Ej: SERVIDOR-APP-01" />

                    <FieldSelect {...FORM.field("type")} label="Tipo" options={OPTIONS_VAULT_ENTRY_TYPE} isSearchable />

                    <FieldSelect {...FORM.field("environment")} label="Entorno" options={ENVIRONMENT_OPTIONS} isSearchable
                        hint="Elegí un entorno estándar. Los valores existentes personalizados se conservan." />

                    <FieldSelect {...FORM.field("parentId")} label="Depende de" options={PARENT_OPTIONS} isSearchable
                        hint={HAS_CHILDREN ? "Este acceso ya es padre; no puede convertirse en hijo." : "Dejalo vacío para el nivel principal. Elegí un padre para crear una única relación padre → hija."}
                        span={3} />


                </FormGrid>

            </FormSection>

            <CredentialSpecializedSections
                idPrefix="company_vault_credential"
                type={FORM.values.type}
                form={FORM}
                technical={useTechnical}
                onTechnicalChange={onChangeTechnical}
                secretPlaceholder={SECRET_PLACEHOLDER}
                includeUrl
                renderSshKey
            />

            <FormSection title="Campos propios" columns={1}
                description="Lo que no entra en los campos de arriba: ID de cuenta, licencia, número de máquina.">

                <div className="LStudio__app_vault_extra_rows">
                    {
                        useExtra.map((row, index) => (
                            <div key={index} className="LStudio__app_vault_extra_row">

                                <div className="LStudio__app_vault_extra_row_head">
                                    <span>Campo personalizado {index + 1}</span>
                                    <AppButton size="sm" variant="ghost" tone="danger" icon={<FaTrash />} isIconOnly
                                        aria-label="Quitar campo" onClick={() => onRemoveExtra(index)} />
                                </div>

                                <FieldInput id={`extra_label_${index}`} label="Nombre"
                                    placeholder="Ej: ID de máquina" value={row.label}
                                    onChange={(event) => onChangeExtra(index, "label", event.target.value)} />

                                <FieldInput id={`extra_value_${index}`} label="Valor"
                                    type={row.isSecret ? "password" : "text"} placeholder="Ej: 102" value={row.value}
                                    onChange={(event) => onChangeExtra(index, "value", event.target.value)} />

                                <div className="LStudio__app_vault_extra_secret">
                                    <FieldSwitch id={`extra_secret_${index}`} label="Secreto"
                                        text="Cifrar" isChecked={row.isSecret}
                                        isDisabled={IS_EDIT && row.isSecret && !revealed}
                                        onChange={(event) => onChangeExtra(index, "isSecret", event.target.checked)} />
                                </div>

                            </div>
                        ))
                    }
                </div>

                <AppButton className="LStudio__app_extra_add_button" size="sm" variant="soft" tone="neutral" icon={<FaPlus />} onClick={onAddExtra}>
                    Agregar campo
                </AppButton>

            </FormSection>

            <FormSection title="Cómo se usa" description="El comando de conexión y la explicación del entorno.">

                <FormGrid columns={3}>

                    <FieldSelect {...FORM.field("commandLanguage")} label="Lenguaje del comando" isSearchable
                        options={OPTIONS_CODE_LANGUAGE} />

                    <FieldCode {...FORM.field("command")} label="Comando de conexión"
                        language={FORM.values.commandLanguage} rows={4}
                        placeholder="psql -h 192.168.100.10 -p 6432 -U admin -d cig" span={3} />

                    <FieldRichText {...FORM.fieldValue("notes", { emptyValue: "" })} label="Notas y aclaraciones" minHeight={200}
                        placeholder="Para ver las credenciales hacemos nano config/database.yml" span={3} />

                </FormGrid>

            </FormSection>

        </AppModal>
    );
};
